import type { TFunction } from 'i18next';
import type { NetworkNode } from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { isSubsidiaryRole, isUpstreamRole, roleSlug } from '@/lib/network-graph';
import type { Locale } from '@/lib/i18n-routes';
import { formatRegisterNumber } from '@/new_components/company-detail/companyDetailUtils';
import type { GraphEdge } from './model';

/** Number/date locale for the page language (de-DE / en-GB). */
export function numberLocale(locale: string) {
  return locale === 'de' ? 'de-DE' : 'en-GB';
}

/** "2016-08-20" or "20.08.2016" → the page locale's date; other input unchanged. */
export function fmtDateString(s: string | null | undefined, locale: string): string {
  if (!s) return '';
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s.trim());
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  const d = de ? new Date(+de[3], +de[2] - 1, +de[1]) : iso ? new Date(+iso[1], +iso[2] - 1, +iso[3]) : null;
  return d
    ? d.toLocaleDateString(numberLocale(locale), { day: '2-digit', month: '2-digit', year: 'numeric' })
    : s;
}

/** Backend roles are German; other locales get the known ones translated. */
export function roleText(role: string, locale: string, t: TFunction): string {
  if (locale.startsWith('de')) return role;
  const slug = roleSlug(role);
  return slug ? t(`network.roles.${slug}`, { defaultValue: role }) : role;
}

/**
 * "60 % · Geschäftsführer(in)": the share when known, then the roles that
 * say more than it (a plain "Gesellschafter(in)" next to a % is noise).
 */
export function edgeLabel(e: GraphEdge, locale: string, t: TFunction): string {
  const roles =
    e.share != null
      ? e.roles.filter((r) => !isUpstreamRole(r) && !isSubsidiaryRole(r))
      : e.roles;
  const shown = roles.slice(0, 2).map((r) => roleText(r, locale, t));
  if (roles.length > 2) shown.push(`+${roles.length - 2}`);
  const share =
    e.share != null
      ? `${new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits: 2 }).format(e.share)} %`
      : null;
  return [share, ...shown].filter(Boolean).join(' · ');
}

/**
 * The same label for the diagram: share and roles on separate lines
 * ("20 %" / "Geschäftsführer(in)") so it stays about as wide as a box.
 */
export function edgeLabelLines(e: GraphEdge, locale: string, t: TFunction): string {
  const full = edgeLabel(e, locale, t);
  if (e.share == null) return full;
  const [share, ...roles] = full.split(' · ');
  return roles.length ? `${share}\n${roles.join(' · ')}` : share;
}

export function courtCity(court: string | null | undefined): string | null {
  const c = (court ?? '').replace(/^Amtsgericht\s+/i, '').trim();
  return c || null;
}

/** Second line of a node box: "Kiel · HRB 23456" for companies, the town for people. */
export function nodeSubline(n: NetworkNode | null): string | null {
  if (!n) return null;
  if (n.node_type === 'person') return n.city ?? null;
  const parts = [
    courtCity(n.register_court),
    formatRegisterNumber(n.register_type, n.register_id),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : (n.city ?? null);
}

/** Company page URL, when the node carries enough register data for a slug. */
export function companyHref(n: NetworkNode | null, locale: Locale): string | null {
  if (!n || n.node_type === 'person' || !n.eu_id || !n.register_court) return null;
  return generateCompanySlug(
    {
      name: n.label,
      registerCourt: n.register_court,
      registerType: n.register_type ?? undefined,
      registerId: n.register_id ?? undefined,
    },
    locale,
  );
}
